// ============================================
// Manor Miser - Socket Manager
// ============================================

import { io, Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
} from '../shared/events';
import type {
  PlayerPublic,
  PlayerPrivate,
  BetResult,
  EliminationResult,
  GameOverResult,
  GameStatePublic,
  BetIncrement,
} from '../shared/types';
import { LocalGameManager } from '../game/LocalGameManager';

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// Event callback types
export interface SocketCallbacks {
  onError?: (message: string, code?: string) => void;
  onRoomCreated?: (roomCode: string, playerId: string, player: PlayerPrivate) => void;
  onRoomJoined?: (roomCode: string, playerId: string, player: PlayerPrivate, players: PlayerPublic[], hostId: string) => void;
  onPlayerJoined?: (player: PlayerPublic) => void;
  onPlayerLeft?: (playerId: string, newHostId?: string) => void;
  onPlayerKicked?: (playerId: string, newHostId?: string) => void;
  onHostChanged?: (newHostId: string) => void;
  onGameStarted?: (gameState: GameStatePublic, yourMoney: number) => void;
  onRoundStarted?: (roundNumber: number, phaseEndsAt: number, yourMoney: number, yourBet: number, alivePlayers: PlayerPublic[]) => void;
  onBetActivity?: (playerId: string) => void;
  onBetUpdated?: (currentBet: number, remainingMoney: number) => void;
  onRoundReveal?: (bets: BetResult[], phaseEndsAt: number) => void;
  onPlayerEliminated?: (result: EliminationResult & { phaseEndsAt: number }) => void;
  onGameOver?: (result: GameOverResult) => void;
  onReturnedToLobby?: (players: PlayerPublic[], hostId: string) => void;
}

class SocketManager {
  private socket: GameSocket | null = null;
  private callbacks: SocketCallbacks = {};
  private serverUrl: string;
  private localGame: LocalGameManager | null = null;

  constructor() {
    // VITE_SERVER_URL overrides. Otherwise: dev (vite) talks to the local server
    // on :3001; production builds use the page's own origin, where nginx proxies
    // /socket.io/ to the server container.
    this.serverUrl =
      import.meta.env.VITE_SERVER_URL ||
      (import.meta.env.DEV ? 'http://localhost:3001' : window.location.origin);
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      console.log('[Socket] Connecting to', this.serverUrl);

      this.socket = io(this.serverUrl, {
        transports: ['websocket', 'polling'],
        timeout: 10000,
      });

      this.socket.on('connect', () => {
        console.log('[Socket] Connected:', this.socket?.id);
        this.registerEventHandlers();
        resolve();
      });

      this.socket.on('connect_error', (error) => {
        console.error('[Socket] Connection error:', error);
        reject(error);
      });

      this.socket.on('disconnect', (reason) => {
        console.log('[Socket] Disconnected:', reason);
      });
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  setCallbacks(callbacks: SocketCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  clearCallbacks(): void {
    this.callbacks = {};
  }

  private registerEventHandlers(): void {
    if (!this.socket) return;

    // Error handler
    this.socket.on('error', ({ message, code }) => {
      console.error('[Socket] Error:', message, code);
      this.callbacks.onError?.(message, code);
    });

    // Lobby events
    this.socket.on('room_created', ({ roomCode, playerId, player }) => {
      console.log('[Socket] Room created:', roomCode);
      this.callbacks.onRoomCreated?.(roomCode, playerId, player);
    });

    this.socket.on('room_joined', ({ roomCode, playerId, player, players, hostId }) => {
      console.log('[Socket] Room joined:', roomCode);
      this.callbacks.onRoomJoined?.(roomCode, playerId, player, players, hostId);
    });

    this.socket.on('player_joined', ({ player }) => {
      console.log('[Socket] Player joined:', player.name);
      this.callbacks.onPlayerJoined?.(player);
    });

    this.socket.on('player_left', ({ playerId, newHostId }) => {
      console.log('[Socket] Player left:', playerId);
      this.callbacks.onPlayerLeft?.(playerId, newHostId);
    });

    this.socket.on('player_kicked', ({ playerId, newHostId }) => {
      console.log('[Socket] Player kicked:', playerId);
      this.callbacks.onPlayerKicked?.(playerId, newHostId);
    });

    this.socket.on('host_changed', ({ newHostId }) => {
      console.log('[Socket] Host changed:', newHostId);
      this.callbacks.onHostChanged?.(newHostId);
    });

    // Game events
    this.socket.on('game_started', ({ gameState, yourMoney }) => {
      console.log('[Socket] Game started');
      this.callbacks.onGameStarted?.(gameState, yourMoney);
    });

    this.socket.on('round_started', ({ roundNumber, phaseEndsAt, yourMoney, yourBet, alivePlayers }) => {
      console.log('[Socket] Round started:', roundNumber);
      this.callbacks.onRoundStarted?.(roundNumber, phaseEndsAt, yourMoney, yourBet, alivePlayers);
    });

    this.socket.on('bet_activity', ({ playerId }) => {
      this.callbacks.onBetActivity?.(playerId);
    });

    this.socket.on('bet_updated', ({ currentBet, remainingMoney }) => {
      this.callbacks.onBetUpdated?.(currentBet, remainingMoney);
    });

    this.socket.on('round_reveal', ({ bets, phaseEndsAt }) => {
      console.log('[Socket] Round reveal');
      this.callbacks.onRoundReveal?.(bets, phaseEndsAt);
    });

    this.socket.on('player_eliminated', (result) => {
      console.log('[Socket] Player eliminated:', result.eliminatedPlayerName);
      this.callbacks.onPlayerEliminated?.(result);
    });

    this.socket.on('game_over', (result) => {
      console.log('[Socket] Game over, winner:', result.winnerName);
      this.callbacks.onGameOver?.(result);
    });

    this.socket.on('returned_to_lobby', ({ players, hostId }) => {
      console.log('[Socket] Returned to lobby');
      this.callbacks.onReturnedToLobby?.(players, hostId);
    });
  }

  // -----------------------------
  // Client Actions
  // -----------------------------

  createRoom(playerName: string): void {
    this.socket?.emit('create_room', { playerName });
  }

  joinRoom(roomCode: string, playerName: string): void {
    this.socket?.emit('join_room', { roomCode, playerName });
  }

  leaveRoom(): void {
    if (this.localGame) {
      this.localGame.destroy();
      this.localGame = null;
      return;
    }
    this.socket?.emit('leave_room');
  }

  kickPlayer(playerId: string): void {
    this.socket?.emit('kick_player', { playerId });
  }

  startGame(): void {
    this.socket?.emit('start_game');
  }

  startSinglePlayer(playerName: string, botCount: number): void {
    // Run locally — no server needed
    this.localGame = new LocalGameManager(
      playerName,
      botCount,
      () => this.callbacks
    );
    this.localGame.startGame();
  }

  addBet(amount: BetIncrement): void {
    if (this.localGame) {
      this.localGame.handleAddBet(amount);
      return;
    }
    this.socket?.emit('add_bet', { amount });
  }

  playAgain(): void {
    if (this.localGame) {
      this.localGame.handlePlayAgain();
      return;
    }
    this.socket?.emit('play_again');
  }

  returnToLobby(): void {
    if (this.localGame) {
      this.localGame.handleReturnToLobby();
      this.localGame = null;
      return;
    }
    this.socket?.emit('return_to_lobby');
  }

  // -----------------------------
  // Getters
  // -----------------------------

  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  get socketId(): string | null {
    if (this.localGame) {
      return this.localGame.playerId;
    }
    return this.socket?.id ?? null;
  }
}

// Singleton instance
export const socketManager = new SocketManager();
